// ============================================
// Wizard State Management
// 마법사 7단계 입력값 관리
// ============================================

// 기본 상태
const DEFAULT_STATE = {
  currentStep: 1,
  totalSteps: 7,

  // Step 1: 성별
  gender: '여성',

  // Step 2: 점술가 설명
  description: '',

  // Step 3: 점술 스타일 태그
  styleTags: [],
  customStyleTags: [],

  // Step 4: 점술 유형 태그
  fortuneTypeTags: [],
  customFortuneTypeTags: [],

  // Step 5: 전문 분야 태그
  specialtyTags: [],
  customSpecialtyTags: [],

  // Step 6: 점술가 배경 (선택적)
  introScene: '',

  // Step 7: 이미지 모델 ('none' = 이미지 생성 안함)
  imageModelId: 'none',

  // 생성 상태
  isGenerating: false
};

// 마법사 상태 (모듈 레벨)
let wizardState = { ...DEFAULT_STATE };

// ============================================
// Getters
// ============================================

export function getWizardState() {
  return { ...wizardState };
}

export function getCurrentStep() {
  return wizardState.currentStep;
}

export function getTotalSteps() {
  return wizardState.totalSteps;
}

export function getSelectedTags() {
  return [
    ...wizardState.styleTags,
    ...wizardState.customStyleTags,
    ...wizardState.fortuneTypeTags,
    ...wizardState.customFortuneTypeTags,
    ...wizardState.specialtyTags,
    ...wizardState.customSpecialtyTags
  ];
}

// ============================================
// Setters
// ============================================

export function setWizardState(updates) {
  wizardState = { ...wizardState, ...updates };
}

export function setCurrentStep(step) {
  wizardState.currentStep = Math.max(1, Math.min(step, wizardState.totalSteps));
}

export function setGender(gender) {
  wizardState.gender = gender;
}

export function setDescription(description) {
  wizardState.description = description;
}

export function setIntroScene(introScene) {
  wizardState.introScene = introScene;
}

export function setImageModelId(modelId) {
  wizardState.imageModelId = modelId;
}

export function setIsGenerating(value) {
  wizardState.isGenerating = value;
}

// ============================================
// 태그 관리 (타입별)
// ============================================

function getTagArrays(tagType) {
  switch (tagType) {
    case 'style':
      return {
        tags: 'styleTags',
        customTags: 'customStyleTags'
      };
    case 'fortuneType':
      return {
        tags: 'fortuneTypeTags',
        customTags: 'customFortuneTypeTags'
      };
    case 'specialty':
      return {
        tags: 'specialtyTags',
        customTags: 'customSpecialtyTags'
      };
    default:
      return {
        tags: 'styleTags',
        customTags: 'customStyleTags'
      };
  }
}

export function toggleTag(tagId, tagType = 'style') {
  const { tags } = getTagArrays(tagType);
  const idx = wizardState[tags].indexOf(tagId);
  if (idx === -1) {
    wizardState[tags].push(tagId);
  } else {
    wizardState[tags].splice(idx, 1);
  }
}

export function addCustomTag(tag, tagType = 'style') {
  const { customTags } = getTagArrays(tagType);
  const trimmed = tag.trim();
  if (trimmed && !wizardState[customTags].includes(trimmed)) {
    wizardState[customTags].push(trimmed);
    return true;
  }
  return false;
}

export function removeCustomTag(tag, tagType = 'style') {
  const { customTags } = getTagArrays(tagType);
  const idx = wizardState[customTags].indexOf(tag);
  if (idx !== -1) {
    wizardState[customTags].splice(idx, 1);
  }
}

// ============================================
// 초기화
// ============================================

export function resetWizardState() {
  wizardState = {
    ...DEFAULT_STATE,
    styleTags: [],
    customStyleTags: [],
    fortuneTypeTags: [],
    customFortuneTypeTags: [],
    specialtyTags: [],
    customSpecialtyTags: []
  };
}

// ============================================
// 단계 유효성 검사
// ============================================

export function isStepValid(step) {
  switch (step) {
    case 1:
      // 성별 필수
      return !!wizardState.gender;
    case 2:
      // 점술가 설명은 선택적
      return true;
    case 3:
      // 점술 스타일 태그는 선택적
      return true;
    case 4:
      // 점술 유형 태그는 선택적
      return true;
    case 5:
      // 전문 분야 태그는 선택적
      return true;
    case 6:
      // 점술가 배경은 선택적
      return true;
    case 7:
      // 이미지 모델 선택 필수 ('none' 포함)
      return !!wizardState.imageModelId;
    default:
      return true;
  }
}

export function canProceedToNext() {
  return isStepValid(wizardState.currentStep);
}

// ============================================
// 진행률 계산
// ============================================

export function getProgressPercent() {
  return (wizardState.currentStep / wizardState.totalSteps) * 100;
}
